import { Bot, Building2, ClipboardList, Database, Handshake, HardHat, History, House, KeyRound, Package, Settings2, Truck, UserRound, Users } from "lucide-react";

import type { NavItem, NavSection } from "@/components/shell/nav";

import { CREW_READ, WORK_ORDER_READ } from "@/pages/workOrders/access";

import { plannedJobsPages, plannedOrderingPages, plannedSalesPages, plannedSections, plannedSettingsPages, plannedTeamPages, type PlannedPage, type PlannedSection } from "./planned";

type Can = (code: string) => boolean;

/**
 * The implemented areas the current membership may use (the API still enforces access), plus the
 * planned areas from app/planned.ts, marked `planned`. Planned areas have no data or permission yet,
 * so they are shown to every member; planned pages inside a built area follow that area's access.
 */
export function tenantSections(slug: string, can: Can): NavSection[] {
  const base = `/t/${slug}`;
  const planned = (pages: PlannedPage[]): NavItem[] => pages.map((p) => ({ label: p.label, to: `${base}/${p.path}`, planned: true }));
  const plannedArea = ({ key, label, icon, pages }: PlannedSection): NavSection => ({ key, label, icon, to: `${base}/${pages[0].path}`, base: `${base}/${key}`, planned: true, items: planned(pages) });
  // Ordering sits between the Jobs and Production areas, as in the discovery functional map.
  const beforeOrdering = new Set(["sales", "jobs"]);
  // The planned Slabs page follows the built pages, then Vendors and Settings.
  const orderingPages = planned(plannedOrderingPages);
  const anyOf = (...codes: string[]) => codes.some((code) => can(code));
  const field = plannedSections.find((s) => s.key === "field")!;
  const measures = anyOf("measurements.schedule", "measurements.record", "measurements.verify");
  // Accounting: invoices, payments and receivables are built (billing.view); the rest is still planned.
  // Payment settings live under Company settings (Stripe returns there); people who can't see that
  // section reach them from here.
  // Subcontractor payables (work orders' approved subcontracted work) need approval and labour costs.
  const payables = can("work_orders.approve") && can("work.view_labor_costs");
  const accounting = (s: PlannedSection): NavSection => ({
    key: "accounting",
    label: s.label,
    icon: s.icon,
    to: `${base}/accounting/${can("billing.view") ? "invoices" : can("accounting.view") ? "journal" : "subcontractor-payables"}`,
    base: `${base}/accounting`,
    items: [
      ...(can("billing.view")
        ? [
            { label: "Invoices", to: `${base}/accounting/invoices`, end: false },
            { label: "Deposits & payments", to: `${base}/accounting/payments`, end: false },
            // Company-wide receivables total every job: they also need "View every job".
            ...(can("jobs.view_all") ? [{ label: "Receivables", to: `${base}/accounting/receivables`, end: false }] : []),
          ]
        : []),
      ...(payables ? [{ label: "Subcontractor payables", to: `${base}/accounting/subcontractor-payables` }] : []),
      ...(can("accounting.view")
        ? [
            { label: "Vendor bills", to: `${base}/accounting/bills` },
            { label: "Money out", to: `${base}/accounting/money-out` },
            { label: "Bank", to: `${base}/accounting/bank` },
            { label: "Journal", to: `${base}/accounting/journal` },
            { label: "Chart of accounts", to: `${base}/accounting/accounts`, end: false },
            { label: "Transaction classes", to: `${base}/accounting/classes` },
            { label: "Statements", to: `${base}/accounting/statements` },
            { label: "Budgets & forecast", to: `${base}/accounting/budgets` },
            { label: "Books settings", to: `${base}/accounting/books` },
          ]
        : []),
      ...(can("payments.manage_settings") && !can("tenant.manage_settings") ? [{ label: "Payment settings", to: `${base}/settings/payments` }] : []),
      ...planned(s.pages),
    ],
  });
  // Work orders and field issues live in Jobs; crews, capacity and the work settings in Schedule.
  const workOrders = anyOf(...WORK_ORDER_READ);
  const crews = anyOf(...CREW_READ);
  // The calendar (schedule.view) with its look-ahead and history; crews and work settings (CREW_READ).
  const calendar = can("schedule.view");
  const schedule = (s: PlannedSection): NavSection => ({
    key: "schedule",
    label: s.label,
    icon: s.icon,
    to: `${base}/schedule${calendar ? "" : "/crews"}`,
    base: `${base}/schedule`,
    items: [
      ...(calendar
        ? [
            { label: "Calendar", to: `${base}/schedule`, end: true },
            { label: "Look-ahead", to: `${base}/schedule/look-ahead` },
          ]
        : []),
      ...(can("schedule.view_history") ? [{ label: "History", to: `${base}/schedule/history` }] : []),
      ...(crews ? [{ label: "Crews & capacity", to: `${base}/schedule/crews`, end: false }, { label: "Work settings", to: `${base}/schedule/work-settings` }] : []),
    ],
  });
  // Production: the shop floor and its report (production.view); stages need production.manage.
  const productionArea = (s: PlannedSection): NavSection => ({
    key: "production",
    label: s.label,
    icon: s.icon,
    to: `${base}/production`,
    base: `${base}/production`,
    items: [
      { label: "Shop floor", to: `${base}/production`, end: true },
      { label: "Daily report", to: `${base}/production/report` },
      ...(can("production.manage") ? [{ label: "Stages", to: `${base}/production/stages` }] : []),
      ...planned(s.pages),
    ],
  });
  // Reports: the operational reports are built (reports.view_operations); the rest is still planned.
  const reportsArea = (s: PlannedSection): NavSection => ({
    key: "reports",
    label: s.label,
    icon: s.icon,
    to: `${base}/reports`,
    base: `${base}/reports`,
    items: [{ label: "Operational reports", to: `${base}/reports` }, ...planned(s.pages)],
  });
  const sections: Array<NavSection | false> = [
    { key: "home", label: "Home", icon: House, to: base, end: true, items: [{ label: "Overview", to: base }] },
    can("clients.view_client") && {
      key: "clients",
      label: "Clients",
      icon: Building2,
      to: `${base}/clients`,
      items: [
        { label: "All clients", to: `${base}/clients` },
        { label: "Archived", to: `${base}/clients/archived` },
      ],
    },
    {
      // Readable with staff access alone (changes need catalog capabilities).
      key: "catalog",
      label: "Catalog",
      icon: Package,
      to: `${base}/catalog/items`,
      base: `${base}/catalog`,
      items: [
        // Not `end`: an item's page keeps the list highlighted.
        { label: "Items", to: `${base}/catalog/items`, end: false },
        { label: "Categories", to: `${base}/catalog/categories` },
        { label: "Manufacturers", to: `${base}/catalog/manufacturers`, end: false },
        // Also under Ordering: the same pages, kept in whichever section they were opened from.
        ...(can("vendors.view_vendor") ? [{ label: "Vendors", to: `${base}/catalog/vendors`, end: false }] : []),
        ...(can("catalog.import_items") && can("catalog.manage_items") ? [{ label: "Import items", to: `${base}/catalog/imports`, end: false }] : []),
      ],
    },
    // Sales: estimates, sales orders and the price book are built; enquiries are still planned.
    anyOf("estimates.view_estimate", "pricing.view_prices", "sales_orders.view_order")
      ? {
          key: "sales",
          label: "Sales",
          icon: Handshake,
          to: `${base}/sales/${can("estimates.view_estimate") ? "estimates" : can("sales_orders.view_order") ? "orders" : "price-book"}`,
          base: `${base}/sales`,
          items: [
            ...planned(plannedSalesPages.slice(0, 1)),
            ...(can("estimates.view_estimate") ? [{ label: "Estimates", to: `${base}/sales/estimates`, end: false }] : []),
            ...(can("sales_orders.view_order") ? [{ label: "Sales orders", to: `${base}/sales/orders`, end: false }] : []),
            ...(can("sales_orders.view_order") && can("sales_orders.release") ? [{ label: "Release orders", to: `${base}/sales/release-orders` }] : []),
            ...(can("pricing.view_prices") ? [{ label: "Price book", to: `${base}/sales/price-book` }] : []),
            ...(anyOf("estimates.manage_estimate", "estimates.manage_group_prices") ? [{ label: "Group prices", to: `${base}/sales/group-prices`, end: false }] : []),
            ...(can("estimates.view_estimate") ? [{ label: "Settings", to: `${base}/sales/settings` }] : []),
          ],
        }
      : { key: "sales", label: "Sales", icon: Handshake, to: `${base}/sales`, planned: true, items: planned(plannedSalesPages) },
    (can("jobs.view_job") || workOrders) && {
      key: "jobs",
      label: "Jobs",
      icon: ClipboardList,
      to: `${base}/jobs${can("jobs.view_job") ? "" : "/work-orders"}`,
      base: `${base}/jobs`,
      items: [
        // Not `end` (a job's page keeps the list highlighted) unless other pages under /jobs are listed.
        ...(can("jobs.view_job") ? [{ label: "All jobs", to: `${base}/jobs`, end: true }] : []),
        ...(workOrders
          ? [
              { label: "Work orders", to: `${base}/jobs/work-orders`, end: false },
              { label: "Field issues", to: `${base}/jobs/field-issues` },
            ]
          : []),
        ...(can("jobs.view_job") ? [{ label: "Documents", to: `${base}/jobs/documents` }] : []),
        ...(can("jobs.view_job") ? planned(plannedJobsPages) : []),
      ],
    },
    {
      // Stock locations are readable with staff access alone; slabs are still planned.
      key: "ordering",
      label: "Ordering",
      icon: Truck,
      to: `${base}/ordering/${can("purchasing.view_orders") ? "purchase-orders" : can("receiving.receive") ? "receiving" : can("inventory.view_stock") ? "inventory" : "locations"}`,
      base: `${base}/ordering`,
      items: [
        ...(can("purchasing.view_orders")
          ? [
              { label: "Purchase demand", to: `${base}/ordering/demand` },
              { label: "Purchasing runs", to: `${base}/ordering/purchasing-runs`, end: false },
              { label: "Purchase orders", to: `${base}/ordering/purchase-orders`, end: false },
            ]
          : []),
        ...(anyOf("purchasing.view_orders", "receiving.receive") ? [{ label: "Deliveries", to: `${base}/ordering/deliveries` }] : []),
        ...(anyOf("receiving.receive", "purchasing.view_orders") ? [{ label: "Receiving", to: `${base}/ordering/receiving`, end: false }] : []),
        ...(anyOf("receiving.receive", "purchasing.view_orders", "receiving.resolve_discrepancies") ? [{ label: "Discrepancies", to: `${base}/ordering/discrepancies`, end: false }] : []),
        ...(can("inventory.view_stock") ? [{ label: "Inventory", to: `${base}/ordering/inventory`, end: false }] : []),
        ...(anyOf("inventory.count_stock", "inventory.approve_counts") ? [{ label: "Stock counts", to: `${base}/ordering/counts`, end: false }] : []),
        { label: "Stock locations", to: `${base}/ordering/locations` },
        ...orderingPages,
        ...(can("vendors.view_vendor") ? [{ label: "Vendors", to: `${base}/ordering/vendors`, end: false }] : []),
        ...(can("operations.manage_settings") ? [{ label: "Settings", to: `${base}/ordering/settings` }] : []),
        // Purchasing automation lives in Company settings › Operations; people who can't see that section reach it here.
        ...(can("operations.manage_fulfillment") && !can("tenant.manage_settings") ? [{ label: "Purchasing automation", to: `${base}/settings/operations` }] : []),
      ],
    },
    ...plannedSections
      .filter((s) => !beforeOrdering.has(s.key))
      .map((s) =>
        s.key === "field" && (measures || can("field.use"))
          ? {
              key: "field",
              label: field.label,
              icon: HardHat,
              // People with the field workspace land on their work; Measurements keeps /field.
              to: `${base}/field${can("field.use") ? "/work" : ""}`,
              base: `${base}/field`,
              items: [
                ...(can("field.use") ? [{ label: "My work", to: `${base}/field/work`, end: false }] : []),
                // `end` while My work is listed: /field is a prefix of its pages.
                ...(measures ? [{ label: "Measurements", to: `${base}/field`, end: can("field.use") }] : []),
                ...planned(field.pages),
              ],
            }
          : s.key === "accounting" && (can("billing.view") || payables || can("accounting.view"))
            ? accounting(s)
            : s.key === "schedule"
              ? (crews || calendar) && schedule(s)
              : s.key === "production" && can("production.view")
                ? productionArea(s)
                : s.key === "reports" && can("reports.view_operations") && can("jobs.view_all")
                ? reportsArea(s)
                : plannedArea(s),
      ),
    can("team.view_membership") && {
      key: "team",
      label: "Team",
      icon: Users,
      to: `${base}/team`,
      items: [
        { label: "Members", to: `${base}/team` },
        { label: "Invitations", to: `${base}/team/invitations` },
        ...planned(plannedTeamPages),
      ],
    },
    can("team.manage_groups") && {
      key: "roles",
      label: "Roles & permissions",
      railLabel: "Roles",
      icon: KeyRound,
      to: `${base}/roles`,
      items: [
        { label: "Roles", to: `${base}/roles` },
        { label: "Permissions", to: `${base}/roles/permissions` },
      ],
    },
    anyOf("tenant.manage_settings", "agent.manage_settings") && {
      key: "settings",
      label: "Company settings",
      railLabel: "Settings",
      icon: Settings2,
      to: `${base}/settings${can("tenant.manage_settings") ? "" : "/ai"}`,
      base: `${base}/settings`,
      items: [
        ...(can("tenant.manage_settings") ? [{ label: "Details & branding", to: `${base}/settings` }] : []),
        ...(can("clients.manage_types") ? [{ label: "Client types", to: `${base}/settings/client-types` }] : []),
        ...(can("payments.manage_settings") ? [{ label: "Payments", to: `${base}/settings/payments` }] : []),
        ...(can("operations.manage_fulfillment") ? [{ label: "Operations", to: `${base}/settings/operations` }, { label: "Policies", to: `${base}/settings/policies` }] : []),
        ...(can("agent.manage_settings") ? [{ label: "AI services", to: `${base}/settings/ai` }] : []),
        ...(can("tenant.manage_settings") ? planned(plannedSettingsPages) : []),
      ],
    },
  ];
  return sections.filter(Boolean) as NavSection[];
}

export function tenantSectionFromPath(path: string): string | null {
  const match = path.match(/^\/t\/[^/]+\/([^/]+)/);
  return match ? match[1] : "home";
}

export type PlatformSectionKey = "tenants" | "users" | "catalog" | "agents" | "impersonation" | "data-model";

/** Platform sections with the platform capability each one requires (the API enforces the same). */
const allPlatformSections: Array<NavSection & { key: PlatformSectionKey; capability: string }> = [
  {
    key: "tenants",
    label: "Tenants",
    icon: Building2,
    to: "/platform/tenants",
    capability: "core.platform_manage_tenants",
    // Not `end`: a tenant's detail page keeps the list highlighted.
    items: [{ label: "All tenants", to: "/platform/tenants", end: false }],
  },
  {
    key: "users",
    label: "Users",
    icon: UserRound,
    to: "/platform/users",
    capability: "core.platform_manage_users",
    items: [{ label: "All users", to: "/platform/users", end: false }],
  },
  {
    key: "catalog",
    label: "Catalog",
    icon: Package,
    to: "/platform/catalog/manufacturers",
    base: "/platform/catalog",
    capability: "core.platform_manage_catalog",
    items: [
      { label: "Manufacturer directory", to: "/platform/catalog/manufacturers" },
      { label: "Supplier directory", to: "/platform/catalog/suppliers" },
      { label: "Directory requests", to: "/platform/catalog/requests" },
      { label: "Products", to: "/platform/catalog/products", end: false },
      { label: "Supplier branches", to: "/platform/catalog/branches" },
      { label: "Standard categories", to: "/platform/catalog/categories" },
    ],
  },
  {
    key: "agents",
    label: "AI assistant",
    railLabel: "Assistant",
    icon: Bot,
    to: "/platform/agents/companies",
    base: "/platform/agents",
    capability: "core.platform_manage_agents",
    items: [
      { label: "Companies", to: "/platform/agents/companies" },
      { label: "Application skill", to: "/platform/agents/skill" },
      { label: "Settings", to: "/platform/agents/settings" },
    ],
  },
  {
    key: "impersonation",
    label: "Impersonation log",
    railLabel: "Imperson\u00adation",
    icon: History,
    to: "/platform/impersonation",
    capability: "core.platform_impersonate",
    items: [{ label: "Sessions", to: "/platform/impersonation" }],
  },
  {
    key: "data-model",
    label: "Data model",
    icon: Database,
    to: "/platform/data-model",
    capability: "core.platform_diagnostics",
    items: [{ label: "Entity relationships", to: "/platform/data-model" }],
  },
];

export function platformSections(capabilities: string[]): NavSection[] {
  return allPlatformSections.filter((s) => capabilities.includes(s.capability)).map(({ capability: _capability, ...section }) => section);
}

/** Whether these platform capabilities open a section (the same map as the navigation). */
export function canOpenPlatformSection(key: PlatformSectionKey, capabilities: string[]): boolean {
  const section = allPlatformSections.find((s) => s.key === key);
  return Boolean(section && capabilities.includes(section.capability));
}

/** Where the platform area starts: the first section these capabilities open, else the profile. */
export function platformHome(capabilities: string[]): string {
  return platformSections(capabilities)[0]?.to ?? "/platform/profile";
}
